import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';

// Components
import Label from '../../components/Label';
import CustomDatePicker from '../../components/CustomDatePicker';
import CustomButton from '../../components/CustomButton';
import { AvailabilityButton, ResumeButton } from '../../components/ActionButtons';
import AvailabilityDetails from '../../components/popup/AvailabilityDetails';
import FormikTextField from '../../components/form/FormikTextField';
import FormikMultiSelect from '../../components/form/FormikMultiSelect';
import FormikDropdown from '../../components/form/FormikDropdown';
import FormikInputNumber from '../../components/form/FormikInputNumber';
import FormikAutocomplete from '../../components/form/FormikAutocomplete';
import CompanyAutocomplete from '../../components/form/CompanyAutocomplete';

// date-fns
import addMinutes from 'date-fns/addMinutes';

// Prime-React Components
import { RadioButton } from 'primereact/radiobutton';
import { classNames } from 'primereact/utils';
import { InputSwitch } from 'primereact/inputswitch';
import { Sidebar } from 'primereact/sidebar';
import { Badge } from 'primereact/badge';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';

// Redux-Actions
import { allInterviewers } from '../../redux/actions/showOptions';
import { editCandidate, getCandidateDetails, addCandidate } from '../../redux/actions/candidates';
import { getInterviewTechnology, getJobDesignationBySearch } from '../../redux/actions/hr';
import { getFilterCommonParamsList } from '../../redux/actions/commonParameter';

// Constants
import {
  BUTTON_TYPES,
  CANDIDATE_LOCATIONS,
  CANDIDATE_SOURCE_TYPE,
  CANDIDATE_SOURCE_REFERENCE_CODE,
  INTERVIEW_CATEGORY,
  Interview_Category,
  INTERVIEW_MODE,
  INTERVIEW_TYPE,
  InterviewMode,
  InterviewType,
  LABEL_TYPES,
  MSG_TYPES,
  queryParamsOfFeedbackParams,
  RENDER
} from '../../constants/common';
import {
  CALL_STATUS_OPTIONS,
  INTERVIEW_DURATION,
  NUMBER_OF_INTERVIEWERS
} from '../../constants/dropdownOptions';

// Redux-Slices
import { apiStatusClear } from '../../redux/slices/apiStatus';
import { setIsUpdatedSuccess, updateData } from '../../redux/slices/candidates';
import { updateCandidateData } from '../../redux/slices/userDetails';

// Utils
import {
  filterFeedbackParams,
  formatAvailabilityTime,
  openResumeLink,
  wordCapitalize
} from '../../utils/common';

// Apis
import { checkEmailMobile } from '../../redux/actions/admin';
import { addEditCandidateJobSchema } from '../../schema/candidate.schema';

const pathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/resume`;
// const profilePathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/profile`;

const AddEditCandidates = (props) => {
  // Props.
  const { onHide, show, ID, handleView } = props;

  // Redux hooks.
  const dispatch = useDispatch();
  const { candidateDetails, isUpdatedSuccess } = useSelector((state) => state.candidates);
  const { isSucceed, isLoading } = useSelector((state) => state.apiStatus);
  const { searchData, interviewTechnologyType } = useSelector((state) => state.hrs);
  const { options, candidates } = useSelector((state) => state);
  const { filterParameters } = useSelector((state) => state.parameter);

  // React useState hooks.
  const [designationOptions, setdesignationOptions] = useState(null);
  const [designationText, setDesignationText] = useState('');
  const [fileName, setFileName] = useState('');
  // const [imageName, setImageName] = useState('');
  const [feedbackParamsOptions, setFeedbackParamsOptions] = useState([]);
  const [startDate, setStartDate] = useState(
    new Date(new Date().setMinutes(new Date().getMinutes() + 30))
  );
  const [interviewerOptions, setInterviewerOptions] = useState(null);
  const [techSearchText, setTechSearchText] = useState(null);
  const [isFile, setIsFile] = useState('');
  // const [isImage, setIsImage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [candidateData, setCandidateData] = useState({
    firstName: '',
    lastName: '',
    gender: 1,
    resumeFilePath: '',
    candidatePhotoPath: '',
    email: '',
    mobile: '',
    secondaryMobile: '',
    yearOfExperience: null,
    monthsOfExperience: null,
    interviewType: '',
    interviewerID: '',
    feedbackParameterIds: [],
    interviewerLimit: NUMBER_OF_INTERVIEWERS[0],
    interviewStartDateTime: '',
    interviewEndDateTime: '',
    duration: 60,
    description: '',
    interviewTechnology: [],
    jobDesignation: '',
    isEdit: true,
    isScheduleInterviewEnabled: false,
    meetingLink: '',
    currentCompany: '',
    currentCTC: null,
    candidateLocation: '',
    location: '',
    interviewRoundMode: InterviewMode['Tatvasoft Office'],
    interviewCategory: Interview_Category.Technical,
    candidateSource: CANDIDATE_SOURCE_TYPE[0]?.code || 1,
    referenceEmployeeName: '',
    callStatus: null,
    screeningCall: ''
  });
  const [isStatusUnlocked, setIsStatusUnlocked] = useState(false);

  // React useRef hooks.
  const fileRef = useRef(null);
  // const imageRef = useRef(null);

  // Functions.
  // const technologiesID = technologies && technologies.split(',');
  const onTogglePopup = () => setShowPopup((prevState) => !prevState);
  const onDateChange = (value) => setStartDate(value);
  const handleDesignationSearch = () => {
    if (designationText) {
      const sanitizeString = designationText.trim().toString();
      dispatch(getJobDesignationBySearch(sanitizeString));
    }
  };

  useEffect(() => {
    if (!techSearchText) {
      const modifiedTechnologyOptions =
        interviewTechnologyType &&
        interviewTechnologyType.find(
          (technology) => technology.ID === candidateData.interviewTechnology
        );
      setTechSearchText(modifiedTechnologyOptions);
    }
  }, [interviewTechnologyType, candidateData]);

  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  const modifiedOptions = (options) => {
    if (!options?.length) return [];
    return options?.map((opt) => {
      return {
        ...opt,
        name: `${wordCapitalize(opt.FirstName)} ${wordCapitalize(opt.LastName)}`,
        search: `${opt.FirstName} ${opt.LastName} [${opt?.UMap?.map((tech) =>
          wordCapitalize(tech?.MapT?.Technology)
        ).join(', ')}]`,
        code: `${opt.ID}`,
        key: `${opt.ID}`,
        disabled: !opt.IsAvailable,
        isAvailable: opt.IsAvailable
      };
    });

    // let optionArray = [];
    // if (Object.keys(options)?.length) {
    //   Object.keys(options).forEach((key) => {
    //     if (options[key]?.length) {
    //       let dividedOptions = options[key]?.map((opt) => {
    //         return {
    //           ...opt,
    //           name: `${opt.FirstName} ${opt.LastName}`,
    //           value: `${opt.ID}`,
    //           disabled: key === 'Hrs' ? false : !opt.IsAvailable,
    //           isAvailable: key === 'Hrs' ? true : opt.IsAvailable,
    //           key: `${opt.ID}`
    //         };
    //       });
    //       optionArray.push({
    //         label: key,
    //         code: key,
    //         items: dividedOptions
    //       });
    //     }
    //   });
    // }
    // return optionArray;
  };

  useEffect(() => {
    if (!ID) {
      const modifiedInterviewerOptions = modifiedOptions(options);
      setInterviewerOptions(modifiedInterviewerOptions);
    }
  }, [options, ID]);

  useEffect(() => {
    const modifiedDesignationOptions =
      searchData && searchData.length > 0 ? searchData : [{ Designation: MSG_TYPES.NO_RESULT }];
    setdesignationOptions(modifiedDesignationOptions);
  }, [searchData]);

  useEffect(() => {
    if (ID && candidateDetails && Object.keys(candidateDetails).length !== 0) {
      let candidateMobile = candidateDetails.Mobile;
      if (!candidateMobile || (candidateMobile && candidateMobile === 'null')) {
        candidateMobile = '';
      }
      let isLocationFromDropdown = CANDIDATE_LOCATIONS.some(
        (location) => location.code === candidateDetails.Address
      );
      setCandidateData((prevState) => ({
        ...prevState,
        isEdit: false,
        firstName: `${candidateDetails.FirstName}`,
        lastName: `${candidateDetails.LastName}`,
        email: candidateDetails.Email,
        mobile: candidateMobile,
        secondaryMobile: candidateDetails?.SecondaryMobile || '',
        gender: candidateDetails.Gender ? parseInt(candidateDetails.Gender) : '',
        yearOfExperience: candidateDetails.YearsOfExperience ?? null,
        monthsOfExperience: candidateDetails.MonthsOfExperience ?? null,
        currentCompany: candidateDetails.CurrentCompany || '',
        currentCTC: candidateDetails.CurrentCTC || null,
        candidateLocation: isLocationFromDropdown ? candidateDetails.Address : 'other',
        location: isLocationFromDropdown ? '' : candidateDetails.Address || '',
        candidateSource: candidateDetails.CandidateSource || '',
        referenceEmployeeName: candidateDetails.EmployeeReferenceName || '',
        callStatus: candidateDetails.CallStatus || null,
        screeningCall: candidateDetails.ScreeningCall || '',
        interviewTechnology:
          candidateDetails.CandidateTechnology && candidateDetails.CandidateTechnology.length > 0
            ? candidateDetails.CandidateTechnology?.map((tech) => tech.ID)
            : []
      }));
      if (candidateDetails?.ResumeOfCandidate?.FilePath) {
        setIsFile(candidateDetails?.ResumeOfCandidate?.FilePath);
      } else {
        setIsFile('');
      }
      setIsStatusUnlocked(false);
    } else if (!ID) {
      setIsStatusUnlocked(true);
    }
  }, [candidateDetails, ID]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [isStatusUnlocked]);

  const clearData = () => {
    onHide(false);
    document.body.classList.remove('sidebar-open');
    setCandidateData({
      firstName: '',
      lastName: '',
      gender: 1,
      resumeFilePath: '',
      candidatePhotoPath: '',
      email: '',
      mobile: '',
      secondaryMobile: '',
      yearOfExperience: null,
      monthsOfExperience: null,
      interviewType: InterviewType['Technical Interview - 1'],
      interviewerID: '',
      feedbackParameterIds: [],
      interviewCategory: Interview_Category.Technical,
      interviewRoundMode: InterviewMode['Tatvasoft Office'],
      interviewerLimit: NUMBER_OF_INTERVIEWERS[0],
      interviewStartDateTime: '',
      interviewEndDateTime: '',
      duration: 60,
      description: '',
      interviewTechnology: [],
      jobDesignation: '',
      isEdit: true,
      isScheduleInterviewEnabled: false,
      meetingLink: '',
      currentCompany: '',
      currentCTC: null,
      candidateLocation: '',
      location: '',
      candidateSource: CANDIDATE_SOURCE_TYPE[0]?.code || 1,
      referenceEmployeeName: '',
      callStatus: null,
      screeningCall: ''
    });
    setFileName('');
    setIsStatusUnlocked(false);
    // setImageName('');
    formik.resetForm();
  };

  useEffect(() => {
    if (show && isUpdatedSuccess && isSucceed) {
      onHide(false);
      formik.resetForm();
      document.body.classList.remove('sidebar-open');
      dispatch(updateData());
      dispatch(updateCandidateData());
      dispatch(apiStatusClear());
      clearData();
      setFileName('');
      // setImageName('');
      setStartDate(null);
      dispatch(setIsUpdatedSuccess(false));
    }
  }, [show, isSucceed, isUpdatedSuccess, dispatch, onHide, clearData]);

  useEffect(
    () => () => {
      dispatch(setIsUpdatedSuccess(false));
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: candidateData,
    validationSchema: addEditCandidateJobSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });
  const meetingLinkRef = useRef(null);

  useEffect(() => {
    if (formik.values.interviewRoundMode === InterviewMode.Online) {
      setTimeout(() => {
        meetingLinkRef.current?.blur();
      }, 0);
    } else {
      formik.setFieldValue('meetingLink', '');
      formik.setFieldTouched('meetingLink', false);
    }
  }, [formik.values.interviewRoundMode]);

  const handleSubmit = (values) => {
    try {
      let selectedFeedbackParameterIds = filterFeedbackParams(
        values.feedbackParameterIds,
        feedbackParamsOptions
      );
      const isOnlineMode = values.interviewRoundMode === InterviewMode.Online;
      let addFormData = {};
      if (!ID && values.isScheduleInterviewEnabled) {
        addFormData = {
          interviewStartDateTime: startDate,
          interviewEndDateTime: addMinutes(startDate, values.duration),
          interviewDuration: values.duration,
          interviewType: values.interviewType,
          ...(values.interviewerID &&
            values.interviewerID?.length && { interviewerID: values.interviewerID }),
          ...(selectedFeedbackParameterIds && {
            feedbackParameters: selectedFeedbackParameterIds
          }),
          interviewerLimit: values.interviewerLimit,
          interviewCategory: values.interviewCategory,
          interviewRoundMode: values.interviewRoundMode,
          ...(values.jobDesignation && { jobDesignation: values.jobDesignation }),
          ...(values.description && { description: values.description }),
          ...(isOnlineMode && values.meetingLink && { meetingLink: values.meetingLink.trim() }),
          isInterview: values.isScheduleInterviewEnabled
        };
      }
      const formData = {
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        gender: values.gender,
        yearOfExperience: values.yearOfExperience,
        monthsOfExperience: values.monthsOfExperience,
        currentCompany: values.currentCompany?.trim(),
        candidateTechnology: values.interviewTechnology,
        currentCTC: values.currentCTC,
        candidateLocation:
          values.candidateLocation === 'other'
            ? values.location?.trim()
            : values.candidateLocation?.trim(),
        candidateSource: values.candidateSource,
        referenceEmployeeName: values.referenceEmployeeName,
        ...((!ID || isStatusUnlocked) && {
          callStatus: values.callStatus,
          screeningCall: values.screeningCall?.trim()
        }),
        ...addFormData
      };

      let postData = new FormData();
      Object.keys(formData).forEach((el) => {
        if (el === 'feedbackParameters') {
          postData.append('feedbackParameters', JSON.stringify(formData[el]));
        } else if (typeof el === 'string') {
          postData.append(el, formData[el]);
        } else {
          postData.append(el, JSON.stringify(formData[el]));
        }
      });

      if (ID) {
        // Only encrypt the mobile number if its provided for the edit candidate process.
        if (values.mobile) {
          postData.append('mobile', values.mobile.trim());
        }
        if (values.secondaryMobile) {
          postData.append('secondaryMobile', values.secondaryMobile.trim());
        }
        postData.append('email', values.email);
        fileName && postData.append('resumeFilePath', values.resumeFilePath);
        // imageName && postData.append('candidatePhotoPath', values.candidatePhotoPath);
        dispatch(
          editCandidate({
            postData,
            CandidateId: ID
          })
        );
      } else {
        if (values.mobile) {
          postData.append('mobile', values.mobile.trim());
        }
        if (values.secondaryMobile) {
          postData.append('secondaryMobile', values.secondaryMobile.trim());
        }
        postData.append('email', values.email);
        fileName && postData.append('resumeFilePath', values.resumeFilePath);
        // imageName && postData.append('candidatePhotoPath', values.candidatePhotoPath);
        dispatch(addCandidate(postData));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name, additionalError = '') => {
    let response = null;
    if (isFormFieldValid(name) || additionalError) {
      response = <small className="p-error">{formik.errors[name] || additionalError}</small>;
    }

    if (name === 'email' && candidates?.existCandidateId) {
      response = (
        <small className="p-error">
          Candidate already added. Click{' '}
          <span
            className="table-view-popup-link"
            onClick={() => handleView(candidates.existCandidateId)}>
            here
          </span>
          for details.
        </small>
      );
    }
    return response;
  };

  useEffect(() => {
    ID && dispatch(getCandidateDetails(ID));
  }, [dispatch, ID]);

  useEffect(() => {
    if (startDate && !ID) {
      const endDate = addMinutes(startDate, formik.values.duration);
      const interviewStartTime = moment(startDate).format();
      const interviewEndTime = moment(endDate).format();
      // const technologies =
      //   technologiesID &&
      //   interviewTechnologyType
      //     .filter((tech) => technologiesID.includes(tech.ID))
      //     .map((tech) => tech.Technology)
      //     .toString();
      dispatch(
        allInterviewers(
          interviewStartTime,
          interviewEndTime,
          formik.values.interviewTechnology?.length ? formik.values.interviewTechnology : '',
          '',
          formik.values.interviewType === InterviewType['HR Round'],
          formik.values.interviewType === InterviewType.Practical
        )
      );
    }
  }, [
    dispatch,
    startDate,
    formik.values.interviewType,
    formik.values.duration,
    formik.values.interviewTechnology
  ]);

  // React useEffect hooks.
  useEffect(() => {
    if (startDate) {
      formik.setFieldValue('interviewerID', []);
    }
  }, [formik.values.interviewType, formik.values.interviewTechnology, startDate]);

  const interviewerTemplate = (option) => {
    return (
      <>
        <ReactTooltip />
        <div data-tip={`${formatAvailabilityTime(option.InterviewerToAvailability)}`}>
          {!option.isAvailable ? (
            <span className="unavailable-interviewer-text-color mr-1">{option.name}</span>
          ) : (
            <span className="mr-1">{option.name}</span>
          )}
          {!!option?.UMap?.length && (
            <span className={`${!option.isAvailable && 'unavailable-interviewer-text-color'} mr-1`}>
              [{option?.UMap?.map((tech) => wordCapitalize(tech?.MapT?.Technology)).join(', ')}]
            </span>
          )}
          {!option.isAvailable && (
            <>
              {formatAvailabilityTime(option.InterviewerToAvailability) && (
                <span className="unavailable-interviewer-text-color mr-1">{`[${formatAvailabilityTime(
                  option.InterviewerToAvailability
                )}]`}</span>
              )}
              {/* <i
                className="pi pi-exclamation-triangle unavailable-interviewer-text-color"
                data-tip="Might not be available for the above schedule selected"
              /> */}
            </>
          )}
          {!!option?.IsPracticalAdmin && (
            <Badge value="Practical Admin" className="practical-admin" severity="success"></Badge>
          )}
        </div>
      </>
    );
  };

  const feedbackParamsOptionsGenerate = (options) => {
    if (!options?.length) return [];

    const committedSingleParams = {
      single: new Set(),
      group: new Set()
    };
    const selectedParameterIds = new Set(formik.values.feedbackParameterIds);

    // Collect committed single parameters
    options.forEach((opt) => {
      if (selectedParameterIds.has(opt.ID)) {
        if (opt.GroupParameters?.length) {
          opt.GroupParameters.forEach((child) =>
            committedSingleParams.group.add(child.Parameter.ID)
          );
        } else {
          committedSingleParams.single.add(opt.ID);
        }
      }
    });

    // Generate parameter options
    const paramsOptions = options.map((opt) => ({
      name: opt.ParameterName,
      code: opt.ID,
      key: opt.ID,
      childParams:
        opt.GroupParameters?.map((child) => ({
          id: child.Parameter.ID || '',
          parameterName: child.Parameter.ParameterName || ''
        })) || [],
      disabled: isOptionDisabled(opt, selectedParameterIds, committedSingleParams)
    }));

    return paramsOptions;
  };

  // Helper function to determine if an option is disabled
  const isOptionDisabled = (opt, selectedParameterIds, committedSingleParams) => {
    if (selectedParameterIds.has(opt.ID)) {
      if (opt.GroupParameters?.length) {
        opt.GroupParameters.forEach((child) => {
          if (selectedParameterIds.has(child.Parameter.ID)) {
            // Remove the child ID from selectedParameterIds
            selectedParameterIds.delete(child.Parameter.ID);
            formik.setFieldValue('feedbackParameterIds', [...selectedParameterIds]);
          }
        });
      }
      return false;
    }

    if (opt.GroupParameters?.length) {
      return opt.GroupParameters.some((child) =>
        committedSingleParams.group.has(child.Parameter.ID)
      );
    }

    return committedSingleParams.single.has(opt.ID) || committedSingleParams.group.has(opt.ID);
  };

  useEffect(() => {
    const modifiedOptions = feedbackParamsOptionsGenerate(filterParameters || []);
    setFeedbackParamsOptions(modifiedOptions);
  }, [filterParameters, formik.values.feedbackParameterIds]);

  useEffect(() => {
    if (show && formik.values.isScheduleInterviewEnabled) {
      dispatch(getFilterCommonParamsList(queryParamsOfFeedbackParams));
    }
  }, [show, formik.values.isScheduleInterviewEnabled]);

  const feedBackParamsTemplate = (option) => {
    return (
      <>
        <span className="mr-1">{wordCapitalize(option.name)}</span>
        {!!option?.childParams?.length && (
          <span>
            [{option?.childParams?.map((tech) => wordCapitalize(tech?.parameterName)).join(', ')}]
          </span>
        )}
      </>
    );
  };

  return (
    <>
      <AvailabilityDetails onHide={onTogglePopup} show={showPopup} />
      <Sidebar
        ID={ID}
        visible={show}
        onHide={() => {
          onHide(true);
          document.body.classList.remove('sidebar-open');
        }}
        closeOnEscape={false}
        position="right"
        className="sidebar-drawer">
        <div className="form-box-wrapper">
          <div className="title-wrapper">
            <p className="card-title">{ID ? 'Edit' : 'Add'} Candidate</p>
            <button className="p-sidebar-close" onClick={() => clearData()}>
              <span className="p-sidebar-close-icon pi pi-times" />
            </button>
          </div>
          <form
            onSubmit={formik.handleSubmit}
            className=""
            autoComplete="off"
            encType="multipart/form-data">
            <div className="form-row-wrapper">
              <div className="form-col full-width">
                <div className="form-row-wrapper">
                  <div className="form-col">
                    <div className="custom-form-group">
                      <FormikTextField
                        labelText={LABEL_TYPES.FIRST_NAME}
                        isMandatory
                        formik={formik}
                        name="firstName"
                        placeholder={LABEL_TYPES.FIRST_NAME + ' *'}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="form-col">
                    <div className="custom-form-group">
                      <FormikTextField
                        labelText={LABEL_TYPES.LAST_NAME}
                        isMandatory
                        formik={formik}
                        name="lastName"
                        placeholder={LABEL_TYPES.LAST_NAME + ' *'}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.EMAIL}
                      isMandatory
                      formik={formik}
                      name="email"
                      placeholder={LABEL_TYPES.EMAIL + ' *'}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        if (e.target.value && !formik.errors.email) {
                          dispatch(
                            checkEmailMobile({
                              email: e.target.value?.trim(),
                              ...(ID && { userId: ID })
                            })
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-row-wrapper">
                  <div className="form-col">
                    <div className="custom-form-group">
                      <FormikTextField
                        labelText={LABEL_TYPES.MOBILE}
                        isMandatory
                        formik={formik}
                        name="mobile"
                        keyfilter="int"
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          if (e.target.value && !formik.errors.mobile) {
                            dispatch(
                              checkEmailMobile({
                                mobile: e.target.value?.trim(),
                                ...(ID && { userId: ID })
                              })
                            );
                          }
                        }}
                        placeholder={LABEL_TYPES.MOBILE + ' *'}
                      />
                    </div>
                  </div>
                  <div className="form-col">
                    <div className="custom-form-group">
                      <FormikTextField
                        labelText={LABEL_TYPES.SECONDARY_MOBILE}
                        formik={formik}
                        name="secondaryMobile"
                        keyfilter="int"
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          if (e.target.value && !formik.errors.secondaryMobile) {
                            dispatch(
                              checkEmailMobile({
                                secondaryMobile: e.target.value?.trim(),
                                ...(ID && { userId: ID })
                              })
                            );
                          }
                        }}
                        placeholder={LABEL_TYPES.SECONDARY_MOBILE}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <CompanyAutocomplete
                      labelText={LABEL_TYPES.CURRENT_COMPANY}
                      isMandatory
                      formik={formik}
                      name="currentCompany"
                      placeholder={`${LABEL_TYPES.CURRENT_COMPANY} *`}
                    />
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikMultiSelect
                      labelText={LABEL_TYPES.TECHNOLOGY}
                      isMandatory
                      formik={formik}
                      name="interviewTechnology"
                      optionLabel="Technology"
                      optionValue="ID"
                      forceSelection
                      options={interviewTechnologyType}
                      placeholder="Select Technology *"
                    />
                  </div>
                </div>
              </div>

              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label htmlFor="currentCTC" text={LABEL_TYPES.CURRENT_CTC} isMandatory />
                    <div className="p-inputgroup w-full">
                      <InputNumber
                        id="currentCTC"
                        name="currentCTC"
                        value={formik.values.currentCTC}
                        placeholder={LABEL_TYPES.CURRENT_CTC}
                        onValueChange={(e) => formik.setFieldValue('currentCTC', e.value)}
                        className={classNames({
                          'p-invalid': isFormFieldValid('currentCTC'),
                          'w-full': true
                        })}
                        mode="decimal"
                        min={0}
                        max={99}
                        maxFractionDigits={2}
                      />
                      <span
                        style={{ backgroundColor: 'inherit' }}
                        className="p-inputgroup-addon add-candidate-lpa">
                        LPA
                      </span>
                    </div>
                  </div>
                  {getFormErrorMessage('currentCTC')}
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikDropdown
                      labelText={LABEL_TYPES.CANDIDATE_CURRENT_LOCATION}
                      isMandatory
                      formik={formik}
                      name="candidateLocation"
                      optionLabel="name"
                      optionValue="code"
                      options={CANDIDATE_LOCATIONS}
                    />
                    {formik.values.candidateLocation === 'other' && (
                      <FormikTextField
                        formik={formik}
                        name="location"
                        placeholder={LABEL_TYPES.CANDIDATE_CURRENT_LOCATION}
                        className="candidate-location-other"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label htmlFor="gender" text={LABEL_TYPES.GENDER} isBold isMandatory />
                    <div className="radio-list-wrapper">
                      <div className="form-group-outer-radiobutton">
                        <RadioButton
                          inputId="male"
                          value={1}
                          name="gender"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.gender === 1}
                        />
                        <Label htmlFor="male" text={LABEL_TYPES.MALE} isBold />
                      </div>
                      <div className="form-group-outer-radiobutton">
                        <RadioButton
                          inputId="female"
                          value={2}
                          name="gender"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.gender === 2}
                        />
                        <Label htmlFor="female" text={LABEL_TYPES.FEMALE} isBold />
                      </div>
                    </div>
                  </div>
                  {getFormErrorMessage('gender')}
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-row-wrapper">
                  <div className="form-col ">
                    <div className="custom-form-group">
                      <FormikInputNumber
                        labelText={LABEL_TYPES.YEAR_EXPERIENCE}
                        isMandatory
                        formik={formik}
                        name="yearOfExperience"
                        placeholder="Years"
                        onValueChange={(e) => formik.setFieldValue('yearOfExperience', e.value)}
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                  <div className="form-col ">
                    <div className="custom-form-group">
                      <FormikInputNumber
                        labelText={LABEL_TYPES.MONTH_EXPERIENCE}
                        isMandatory
                        formik={formik}
                        name="monthsOfExperience"
                        placeholder="0-11"
                        onValueChange={(e) => formik.setFieldValue('monthsOfExperience', e.value)}
                        min={0}
                        max={11}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikDropdown
                      labelText={LABEL_TYPES.CANDIDATE_SOURCE}
                      isMandatory
                      formik={formik}
                      name="candidateSource"
                      optionLabel="name"
                      optionValue="code"
                      options={CANDIDATE_SOURCE_TYPE}
                    />
                  </div>
                </div>
              </div>
              {formik.values.candidateSource === CANDIDATE_SOURCE_REFERENCE_CODE && (
                <>
                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikTextField
                          labelText={LABEL_TYPES.CANDIDATE_SOURCE_REFERENCE}
                          formik={formik}
                          name="referenceEmployeeName"
                          placeholder={LABEL_TYPES.CANDIDATE_SOURCE_REFERENCE}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <div className="flex align-items-center">
                      <div className="flex-1">
                        <FormikDropdown
                          labelText={LABEL_TYPES.CALL_STATUS}
                          isMandatory
                          formik={formik}
                          name="callStatus"
                          optionLabel="name"
                          optionValue="code"
                          options={CALL_STATUS_OPTIONS}
                          placeholder="Select Status of call *"
                          disabled={ID ? !isStatusUnlocked : false}
                        />
                      </div>
                      {ID && !isStatusUnlocked && (
                        <div
                          className="ml-3 cursor-pointer mt-4"
                          onClick={() => {
                            setIsStatusUnlocked(true);
                            formik.setFieldValue('callStatus', null);
                            formik.setFieldValue('screeningCall', '');
                          }}
                          data-tip="Click to edit and log a new call status">
                          <i
                            className="pi pi-pencil"
                            style={{
                              fontSize: '1.2rem',
                              color: '#757575'
                            }}></i>
                        </div>
                      )}
                      {ID && isStatusUnlocked && (
                        <div
                          className="ml-3 cursor-pointer mt-4"
                          onClick={() => {
                            setIsStatusUnlocked(false);
                            formik.setFieldValue(
                              'callStatus',
                              candidateDetails?.CallStatus || null
                            );
                            formik.setFieldValue(
                              'screeningCall',
                              candidateDetails?.ScreeningCall || ''
                            );
                          }}
                          data-tip="Cancel status update and revert changes">
                          <i
                            className="pi pi-times"
                            style={{
                              fontSize: '1.2rem',
                              color: '#dc3545'
                            }}></i>
                        </div>
                      )}
                      <ReactTooltip />
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label htmlFor="screeningCall" text={LABEL_TYPES.SCREENING_CALL} />
                    <InputTextarea
                      id="screeningCall"
                      name="screeningCall"
                      rows={3}
                      cols={30}
                      value={formik.values.screeningCall}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      autoResize
                      placeholder={LABEL_TYPES.SCREENING_CALL}
                      disabled={ID ? !isStatusUnlocked : false}
                      className={classNames({
                        'p-invalid': isFormFieldValid('screeningCall'),
                        'w-full': true
                      })}
                      maxLength={5000}
                    />
                    {getFormErrorMessage('screeningCall')}
                  </div>
                </div>
              </div>
              {!ID && (
                <div className="form-col full-width">
                  <div className="form-group-outer">
                    <div className="custom-form-group">
                      <div className="wrapper-text">
                        <Label
                          htmlFor="isScheduleInterviewEnabled"
                          text={LABEL_TYPES.SCHEDULE_INTERVIEW}
                          isBold
                        />
                        <InputSwitch
                          name="isScheduleInterviewEnabled"
                          checked={formik.values.isScheduleInterviewEnabled}
                          onChange={formik.handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {formik.values.isScheduleInterviewEnabled && (
                <>
                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <CustomDatePicker
                          value={startDate}
                          onChange={onDateChange}
                          minDateTime={new Date().setDate(new Date().getDate())}
                          minTime={new Date().setHours(0, 0, 0)}
                          maxTime={new Date().setHours(23, 59, 59)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikDropdown
                          labelText={LABEL_TYPES.INTERVIEW_TYPE}
                          isMandatory
                          formik={formik}
                          name="interviewType"
                          optionLabel="name"
                          optionValue="code"
                          options={INTERVIEW_TYPE}
                          placeholder="Select Interview Type *"
                          onChange={(e) => {
                            formik.handleChange(e);
                            if (isFirstTime && formik.values.interviewTechnology?.length) {
                              formik.setFieldValue(
                                'feedbackParameterIds',
                                formik.values.interviewTechnology || []
                              );
                            } else {
                              formik.setFieldValue('feedbackParameterIds', []);
                            }
                          }}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikDropdown
                          labelText={LABEL_TYPES.INTERVIEW_ROUND_MODE}
                          isMandatory
                          formik={formik}
                          name="interviewRoundMode"
                          optionLabel="name"
                          optionValue="code"
                          options={INTERVIEW_MODE}
                          placeholder="Select Interview Mode"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikDropdown
                          labelText={LABEL_TYPES.INTERVIEW_CATEGORY}
                          isMandatory
                          formik={formik}
                          name="interviewCategory"
                          optionLabel="name"
                          optionValue="code"
                          options={INTERVIEW_CATEGORY}
                        />
                      </div>
                    </div>
                  </div>
                  {RENDER.No && (
                    <div className="form-col full-width">
                      <div className="form-group-outer">
                        <div className="custom-form-group">
                          <FormikAutocomplete
                            labelText={LABEL_TYPES.DESIGNATION}
                            isMandatory
                            formik={formik}
                            name="jobDesignation"
                            value={designationText}
                            onChange={(e) => {
                              setDesignationText(e.target.value);
                              formik.setFieldValue('jobDesignation', e.target.value?.ID);
                            }}
                            placeholder={LABEL_TYPES.DESIGNATION}
                            suggestions={designationOptions}
                            completeMethod={handleDesignationSearch}
                            forceSelection
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikDropdown
                          labelText={LABEL_TYPES.INTERVIEW_DURATION}
                          isMandatory
                          formik={formik}
                          name="duration"
                          optionLabel="name"
                          optionValue="code"
                          options={INTERVIEW_DURATION}
                          placeholder="Select Interview Duration"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikDropdown
                          labelText={LABEL_TYPES.INTERVIEWER_LIMIT}
                          isMandatory
                          formik={formik}
                          name="interviewerLimit"
                          options={NUMBER_OF_INTERVIEWERS}
                          placeholder="Select Number of Interviewers"
                          className="w-full custom-dropdown"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <Label
                          htmlFor="interviewerID"
                          text={LABEL_TYPES.INTERVIEWERS}
                          isMandatory
                          isBold
                          isChildren
                          tooltip="Please select Date and Time (time must be between 8 AM to 8 PM)"
                        />
                        <AvailabilityButton onClick={onTogglePopup} />
                        <FormikMultiSelect
                          isMandatory
                          formik={formik}
                          name="interviewerID"
                          placeholder="Select Interviewer *"
                          optionLabel="name"
                          optionValue="code"
                          options={interviewerOptions}
                          forceSelection
                          itemTemplate={interviewerTemplate}
                          filterBy="search"
                        />
                      </div>
                    </div>
                  </div>
                  {formik.values.interviewRoundMode === InterviewMode.Online && (
                    <div className="form-col full-width">
                      <div className="form-group-outer">
                        <div className="custom-form-group">
                          <FormikTextField
                            labelText="Interview Meeting Link"
                            isMandatory
                            formik={formik}
                            name="meetingLink"
                            type="url"
                            placeholder="https://meet.google.com/..."
                            className="w-full"
                            ref={meetingLinkRef}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(formik.values.interviewType === InterviewType['Technical Interview - 1'] ||
                    formik.values.interviewType === InterviewType['Technical Interview - 2'] ||
                    formik.values.interviewType === InterviewType['Management Round']) && (
                    <div className="form-col full-width">
                      <div className="form-group-outer">
                        <div className="custom-form-group">
                          <FormikMultiSelect
                            labelText={LABEL_TYPES.FEEDBACK_PARAMETERS}
                            isMandatory
                            formik={formik}
                            name="feedbackParameterIds"
                            placeholder="Select Feedback Params"
                            optionLabel="name"
                            optionValue="code"
                            options={feedbackParamsOptions}
                            onChange={(e) => {
                              formik.handleChange(e);
                              setIsFirstTime(false);
                            }}
                            itemTemplate={feedBackParamsTemplate}
                            showSelectAll={false}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* <div className="form-col full-width">
                <div className="form-group-outer upload-document-wrapper">
                  <div className="custom-form-group">
                    <Label
                      htmlFor="file"
                      text={
                        !ID
                          ? BUTTON_TYPES.UPLOAD_CANDIDATE_IMAGE
                          : BUTTON_TYPES.UPDATE_CANDIDATE_IMAGE
                      }
                      isBold
                    />
                    <input
                      hidden
                      ref={imageRef}
                      type="file"
                      id="candidatePhotoPath"
                      name="candidatePhotoPath"
                      onChange={(e) => {
                        formik.setFieldValue('candidatePhotoPath', e.target.files[0]);
                        setImageName(e.target.files[0].name);
                        setIsImage('');
                      }}
                      accept="image/png, image/jpeg ,image/jpg"
                      className={classNames({
                        'p-invalid': isFormFieldValid('candidatePhotoPath')
                      })}
                    />
                  </div>
                  <div className="flex flex-wrap align-items-center">
                    <CustomButton
                      variant="contained"
                      className="gray-btn"
                      onClick={() => {
                        imageRef.current.click();
                      }}>
                      <div>
                        <i className="pi pi-fw pi-upload mr-1" />
                        <span>
                          {!ID
                            ? BUTTON_TYPES.UPLOAD_CANDIDATE_IMAGE
                            : BUTTON_TYPES.UPDATE_CANDIDATE_IMAGE}
                        </span>
                      </div>
                    </CustomButton>
                    {ID ? (
                      isImage ? (
                        <ResumeButton
                          onClick={() => openResumeLink(`${profilePathBase}/${ID}/${isImage}`)}
                        />
                      ) : (
                        <p>{imageName}</p>
                      )
                    ) : (
                      <p>{imageName}</p>
                    )}
                  </div>
                </div>
                {getFormErrorMessage('candidatePhotoPath')}
              </div> */}
              <div className="form-col full-width">
                <div className="form-group-outer upload-document-wrapper">
                  <div className="custom-form-group">
                    <Label
                      htmlFor="file"
                      text={!ID ? LABEL_TYPES.UPLOAD_RESUME : LABEL_TYPES.UPDATE_RESUME}
                      isBold
                    />
                    <input
                      hidden
                      ref={fileRef}
                      type="file"
                      id="resumeFilePath"
                      name="resumeFilePath"
                      onChange={(e) => {
                        formik.setFieldValue('resumeFilePath', e.target.files[0]);
                        setFileName(e.target.files[0].name);
                        setIsFile('');
                      }}
                      accept=".doc, .docx, .pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className={classNames({
                        'p-invalid': isFormFieldValid('resumeFilePath')
                      })}
                    />
                  </div>
                  <div className="flex flex-wrap align-items-center">
                    <CustomButton
                      variant="contained"
                      className="gray-btn"
                      onClick={() => {
                        fileRef.current.click();
                      }}>
                      <div>
                        <i className="pi pi-fw pi-upload mr-1" />
                        <span>{!ID ? BUTTON_TYPES.UPLOAD_RESUME : BUTTON_TYPES.UPDATE_RESUME}</span>
                      </div>
                    </CustomButton>
                    {ID ? (
                      isFile ? (
                        <ResumeButton
                          onClick={() => openResumeLink(`${pathBase}/${ID}/${isFile}`)}
                        />
                      ) : (
                        <p>{fileName}</p>
                      )
                    ) : (
                      <p>{fileName}</p>
                    )}
                  </div>
                </div>
                {getFormErrorMessage('resumeFilePath')}
              </div>
            </div>

            <div className="form-btn-wrapper">
              <CustomButton type="submit" variant="contained" disabled={isLoading}>
                {BUTTON_TYPES.SAVE}
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={() => clearData()}
                color="error"
                className="gray-btn">
                {BUTTON_TYPES.CANCEL}
              </CustomButton>
            </div>
          </form>
        </div>
      </Sidebar>
    </>
  );
};

export default AddEditCandidates;
